import React, { useState, useRef, useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { Input } from '@frontend/shadcn/components/ui/input';
import { cn } from '@/shadcn/lib/utils';
import { SearchQueryFormData } from '../hooks/use-filters-form';
import { QueryVariables } from '../types';

interface CreatorInputProps {
  form: UseFormReturn<SearchQueryFormData>;
  placeholder?: string;
  disabled?: boolean;
  maxItems?: number;
  className?: string;
  updateUrlParams: (variables: QueryVariables) => void;
  handleSearch: () => void;
  queryVariables: QueryVariables;
}

export const CreatorInput: React.FC<CreatorInputProps> = ({
  form,
  placeholder = 'Enter a creator username to find similar creators (up to 5 creators)',
  disabled = false,
  maxItems = 5,
  className,
  updateUrlParams,
  handleSearch,
  queryVariables,
}) => {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const currentValues = form.watch('similarToCreators') || [];

  const addCreator = useCallback((username: string) => {
    const trimmedUsername = username.trim();
    if (!trimmedUsername) return;

    const currentList = form.getValues('similarToCreators') || [];

    // Check if already exists (case insensitive)
    if (currentList.some((existing) => existing.toLowerCase() === trimmedUsername.toLowerCase())) {
      return;
    }

    // Check max limit
    if (currentList.length >= maxItems) {
      return;
    }

    const newList = [...currentList, trimmedUsername];
    form.setValue('similarToCreators', newList, { shouldValidate: true });
    setInputValue('');

    // Update query params and submit search
    updateUrlParams({
      ...queryVariables,
      similarToCreators: newList,
      searchContext: 'similar-creators',
      query: '', // Clear the query when using similarToCreators
      // Reset pagination
      before: null,
      after: null,
    });
    handleSearch();
  }, [form, maxItems, updateUrlParams, handleSearch, queryVariables]);

  const removeCreator = useCallback((indexToRemove: number) => {
    const currentList = form.getValues('similarToCreators') || [];
    const newList = currentList.filter((_, index) => index !== indexToRemove);
    form.setValue('similarToCreators', newList, { shouldValidate: true });

    // Update query params and submit search
    updateUrlParams({
      ...queryVariables,
      similarToCreators: newList,
      searchContext: 'similar-creators',
      query: '', // Clear the query when using similarToCreators
      // Reset pagination
      before: null,
      after: null,
    });
    handleSearch();
  }, [form, updateUrlParams, handleSearch, queryVariables]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ',' || e.key === ' ') {
      e.preventDefault();
      addCreator(inputValue);
    } else if (e.key === 'Backspace' && inputValue === '' && currentValues.length > 0) {
      // Remove last item when backspace is pressed on empty input
      removeCreator(currentValues.length - 1);
    } else if (e.key === 'Enter') {
      // Only handle Enter for similar-creators context
      if (queryVariables.searchContext === 'similar-creators') {
        e.preventDefault();
        if (inputValue.trim()) {
          addCreator(inputValue);
        } else if (currentValues.length > 0) {
          // If input is empty but we have creators, trigger search
          handleSearch();
        }
      }
      // For other contexts, let Enter bubble up to trigger form submission
    }
  };

  const handleInputBlur = () => {
    if (inputValue.trim()) {
      addCreator(inputValue);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const usernames = pastedText.split(/[,\s]+/).map((username) => username.trim()).filter((username) => username !== '');

    if (usernames.length === 0) return;

    const currentList = form.getValues('similarToCreators') || [];
    const newUsernamesFiltered = usernames.filter((username) => {
      // Check if already exists (case insensitive)
      return !currentList.some((existing) => existing.toLowerCase() === username.toLowerCase());
    });

    if (newUsernamesFiltered.length === 0) return;

    // Limit to remaining slots
    const remainingSlots = maxItems - currentList.length;
    const usernamesToAdd = newUsernamesFiltered.slice(0, remainingSlots);

    if (usernamesToAdd.length === 0) return;

    const newList = [...currentList, ...usernamesToAdd];
    form.setValue('similarToCreators', newList, { shouldValidate: true });

    // Update query params and submit search once
    updateUrlParams({
      ...queryVariables,
      similarToCreators: newList,
      searchContext: 'similar-creators',
      query: '', // Clear the query when using similarToCreators
      // Reset pagination
      before: null,
      after: null,
    });
    handleSearch();
  };

  const fieldError = form.formState.errors.similarToCreators;
  const isAtMaxLimit = currentValues.length >= maxItems;

  return (
    <div className={cn('space-y-2', className)}>
      {/* Input Field */}
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          placeholder={isAtMaxLimit ? `Maximum ${maxItems} creators allowed` : placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleInputBlur}
          onPaste={handlePaste}
          className="w-full border-none shadow-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-0"
          disabled={disabled || isAtMaxLimit}
          aria-label="Add creator username"
          aria-describedby={fieldError ? 'creator-input-error' : undefined}
        />

        {/* Counter */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500">
          {currentValues.length}/{maxItems}
        </div>
      </div>

      {/* Error Message */}
      {fieldError && (
        <p id="creator-input-error" className="text-sm text-red-600" role="alert">
          {fieldError.message}
        </p>
      )}
    </div>
  );
};
